import type { ThemePalette, ThemeTokens } from './types';

const scheme = (
  primary: string, primaryContainer: string, secondary: string, tertiary: string,
  background: string, surface: string, surfaceVariant: string, text: string, muted: string,
): ThemeTokens => ({
  primary, onPrimary: '#ffffff', primaryContainer, onPrimaryContainer: text,
  secondary, tertiary, background, surface, surfaceVariant, text, muted, outline: secondary,
});

export const THEMES: ThemePalette[] = [
  {
    id: 'hatsune', label: '初音配色', description: '明亮藍綠搭一點粉色。',
    preview: ['#66ccff', '#4a9fcc', '#e0f4ff', '#8b9daf', '#ffb6c1'],
    light: scheme('#2895c7', '#d7f2ff', '#72899a', '#d85f78', '#eaf8ff', '#f8fdff', '#d6eaf4', '#1a1c1e', '#44505a'),
    dark: scheme('#66ccff', '#204c63', '#b0c8d8', '#ff9daf', '#0e1b24', '#172630', '#314553', '#e3e6ea', '#c4cbd4'),
  },
  {
    id: 'classic_blue', label: '經典深淺藍', description: '深藍有工具感，淺藍背景乾淨。',
    preview: ['#011f4b', '#03396c', '#005b96', '#6497b1', '#b3cde0'],
    light: scheme('#005b96', '#d5e7f2', '#6497b1', '#0083b3', '#e7eef5', '#f7fafc', '#d6e4ec', '#172033', '#506477'),
    dark: scheme('#8fc8e8', '#244f72', '#9ab7c8', '#45c1ed', '#0b1620', '#13202b', '#2d4252', '#e3edf4', '#b9c8d3'),
  },
  {
    id: 'standard_blue_gray', label: '標準藍灰', description: '灰階更多、藍感低一點，整體內斂。',
    preview: ['#6e7f80', '#536872', '#708090', '#536878', '#36454f'],
    light: scheme('#536878', '#dde6e8', '#708090', '#c27a00', '#e8eff0', '#f8fafa', '#dce5e7', '#1c272d', '#52636b'),
    dark: scheme('#aec3ca', '#3c515b', '#b5c4c9', '#ffc46a', '#101619', '#182126', '#334147', '#e5ecee', '#b8c4c8'),
  },
  {
    id: 'figma_blue_gray', label: 'Figma 藍灰', description: '偏冷、偏產品介面，不會太暗。',
    preview: ['#263a4d', '#46627d', '#6a89a7', '#afc1d2', '#eef3f7'],
    light: scheme('#46627d', '#d9e6f0', '#6a89a7', '#d94e4e', '#eaf1f6', '#f8fbfd', '#d9e4ec', '#172433', '#536778'),
    dark: scheme('#afcbe5', '#39546d', '#b2c4d3', '#ff9a9a', '#0e151d', '#17212b', '#2d3e4f', '#e5edf4', '#b7c4d0'),
  },
  {
    id: 'mist_blue_gray', label: '淡霧藍灰', description: '很亮、很柔，適合長時間看。',
    preview: ['#12243d', '#5d6f88', '#8a9cb5', '#b7c9e2', '#f8fafc'],
    light: scheme('#3f516a', '#dde7f2', '#7589a3', '#536f96', '#eef3fa', '#fbfcfe', '#dde6f0', '#17243a', '#5c6879'),
    dark: scheme('#bcd0ea', '#3f516a', '#aebbd0', '#92a9c9', '#0e141d', '#171f2a', '#344252', '#e8edf5', '#b9c2d0'),
  },
  {
    id: 'dusty_blue_gray', label: 'Dusty 藍灰', description: '藍灰混一點暖灰，不會冷冰冰。',
    preview: ['#4e5a65', '#748a9e', '#b5c9d5', '#beb8b1', '#f6f5f2'],
    light: scheme('#4e5a65', '#e0e8ec', '#748a9e', '#a85f42', '#eceff1', '#fafaf8', '#e0e7ea', '#242a2f', '#626b71'),
    dark: scheme('#b5c9d5', '#43545f', '#b7c2c9', '#e4a17f', '#15181b', '#1f2428', '#3c454c', '#ebedef', '#bdc3c7'),
  },
  {
    id: 'night_blue_gray', label: '深夜藍灰', description: '深色骨架明確，畫面俐落。',
    preview: ['#121e31', '#45526d', '#5c6776', '#6b788b', '#9da5ae'],
    light: scheme('#45526d', '#dde3ea', '#6b788b', '#4b7daa', '#e7eaee', '#f7f8fa', '#dce2ea', '#1b2230', '#586170'),
    dark: scheme('#b8c4d9', '#45526d', '#aeb8c8', '#8ec5f1', '#0c1119', '#151b25', '#313a48', '#e7ebf2', '#b5bdc9'),
  },
  {
    id: 'blue_gray_yellow', label: '藍灰亮黃', description: '藍灰當主體，黃色做同步與提醒。',
    preview: ['#3bb6d5', '#35a3bf', '#879ba6', '#bbbbbb', '#ffc40e'],
    light: scheme('#237f96', '#ddf1f6', '#718892', '#b98600', '#eaf5f8', '#f9fcfd', '#ddeef3', '#173038', '#526b74'),
    dark: scheme('#8edcef', '#236d80', '#b5c5cc', '#ffd55a', '#0e171a', '#162226', '#30464e', '#e4eef0', '#b8c8cc'),
  },
  {
    id: 'green_blue_gray', label: '灰綠藍', description: '自然感較多，比純藍更放鬆。',
    preview: ['#6e7a73', '#9ea6a2', '#9eb5b3', '#cedad9', '#f5f8f7'],
    light: scheme('#586d64', '#dde9e7', '#7f9b96', '#397d76', '#edf3f2', '#fbfcfb', '#dde8e6', '#22302a', '#5a6963'),
    dark: scheme('#b9cdc9', '#50615c', '#b8c8c5', '#91c7c1', '#0f1513', '#18201d', '#33423e', '#e5ece9', '#b9c5c1'),
  },
  {
    id: 'turquoise_slate', label: '海松石灰藍', description: '有水感、清爽，但不會太可愛。',
    preview: ['#144772', '#3d6886', '#3689a6', '#93b9c6', '#f3f8fa'],
    light: scheme('#3d6886', '#d9ebf1', '#769da9', '#168c83', '#e7f1f4', '#f8fbfc', '#d9e9ee', '#152c3a', '#536b75'),
    dark: scheme('#9bd3e8', '#3d6886', '#b0cbd4', '#68e0d5', '#0b1418', '#142129', '#2f4650', '#e4edf1', '#b5c6cd'),
  },
  {
    id: 'mist_purple_blue', label: '霧紫灰藍', description: '藍灰加一點紫灰，有柔霧感。',
    preview: ['#333a56', '#52607a', '#7f8ca6', '#c0c6d2', '#f4f1f6'],
    light: scheme('#52607a', '#e1e5ef', '#7f8ca6', '#a65f8f', '#eceef4', '#fcfbfd', '#e0e4ee', '#25283a', '#626779'),
    dark: scheme('#c1cae0', '#52607a', '#b8c0d3', '#d9a2c9', '#111219', '#1b1f29', '#383e50', '#ececf2', '#bec0cc'),
  },
];

export function applyTheme(themeId: string, mode: 'system' | 'light' | 'dark') {
  const theme = THEMES.find((item) => item.id === themeId) ?? THEMES[0];
  const resolvedMode = mode === 'system'
    ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
    : mode;
  const tokens = theme[resolvedMode];
  const root = document.documentElement;
  root.dataset.mode = resolvedMode;
  root.style.colorScheme = resolvedMode;
  Object.entries(tokens).forEach(([key, value]) => root.style.setProperty(`--${key.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}`, value));
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', tokens.background);
}
